// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 85223095-3baa-4e85-89a2-199b28d0cd3b

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Chip, Tooltip, Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  allFieldsFor, FACT_SHEET_TYPES, FIELD_TYPES, PICKLISTS, resolvePicklist, resolveDrTier,
  TAG_GROUPS, typeMeta,
} from './schema';
import { exportCsv, printElementToPdf } from './exports';
import store from './store';
import HelpBubble from './HelpBubble';

// ---------------------------------------------------------------------------
// Primordia — Application Landscape (LeanIX-style nested heat map).
//
// Fact sheets are bucketed by ONE dimension (the group-by) and every tile is
// coloured by ANOTHER (the colour measure). Both lists are derived from the
// schema at runtime rather than hardcoded: the previous build offered five
// dimensions, three of them tag-driven, so the report read as empty for any
// tenant whose tags were never populated while business owner, criticality,
// hosting, TIME, 6R, wave, fit and RTO/RPO — the cuts a client actually asks
// for — were unreachable.
//
// Rules this screen holds itself to:
//   * "Not set" is a real bucket, never a dropped record, and never shares a
//     colour with a real value. During discovery it is usually the point.
//   * An option is never silently missing. Dimensions that cannot be charted
//     (too many distinct values, free-form prose, continuous numbers) stay in
//     the menu, disabled, with the reason.
//   * Every number on screen is qualified: group sizes, a total, and
//     "N of M records charted" so a capped view can never read as the estate.
//   * DR Tier always goes through resolveDrTier() — it lives in a tag on some
//     records and in a field on others.
// ---------------------------------------------------------------------------

const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

const NOT_SET = 'Not set';
// Deliberately outside the categorical palette: an unfilled bucket must never
// be mistaken for a real value at a glance.
const NOT_SET_COLOR = '#9AA6B2';
const UNKNOWN_COLOR = '#C6D0D9';

const MAX_GROUPS = 40;           // rendered group tiles; overflow is summarised
const MAX_TILES_PER_GROUP = 200; // rendered fact-sheet tiles inside one group
const MAX_LEGEND = 14;
const TEXT_DISTINCT_LIMIT = 40;  // free-text dimensions above this are disabled

const ALL_TYPES = '__all';

// Optimum palette for categorical colouring (no greys — see NOT_SET_COLOR).
const PALETTE = [
  '#0D5B9A', '#021F4B', '#2E7D32', '#F5A623', '#C62828',
  '#6A1B9A', '#00838F', '#EF6C00', '#43A047', '#283593',
  '#8E24AA', '#0B5FFF',
];

// red -> amber -> green across n buckets (ordinal scales: fit, 6R, completion)
const scaleColor = (idx, n) => {
  const stops = ['#D0021B', '#F5A623', '#F8E71C', '#7ED321', '#2E7D32'];
  if (n <= 1) return stops[4];
  const t = idx / (n - 1);
  return stops[Math.round(t * (stops.length - 1))];
};

// Colour maps we keep verbatim from the previous build so saved screenshots and
// client decks stay readable.
const SEMANTIC_COLORS = {
  timeClassification: {
    Invest: '#2E7D32', Tolerate: '#0B5FFF', Migrate: '#F5A623', Eliminate: '#D0021B',
  },
};

// Picklists that read as a scale rather than a set of peers.
const ORDINAL_KEYS = [
  'functionalFit', 'technicalFit', 'businessCriticality', 'sixRClassification',
  'cmDisposition', 'patientSafetyImpact', 'rto', 'rpo', 'lifecycleState',
  '__completion', '__drTier',
];

const PCT_BANDS = ['0%', '1–39%', '40–74%', '≥75%'];

// ---- value access ----------------------------------------------------------
const asText = (v) => {
  if (v === undefined || v === null) return NOT_SET;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    const s = v.filter((x) => x !== null && x !== undefined && String(x).trim() !== '').join(', ');
    return s || NOT_SET;
  }
  const s = String(v).trim();
  return s === '' ? NOT_SET : s;
};

const fieldText = (fs, key) => asText(fs.fields ? fs.fields[key] : undefined);

const tagText = (fs, group) => {
  const vals = (fs.tags || [])
    .filter((t) => t && t.group === group && t.value)
    .map((t) => t.value);
  return vals.length ? vals.join(', ') : NOT_SET;
};

// A percent that was never captured is NOT the same as a percent of zero, and
// the difference is exactly what a discovery lead is looking for.
const bandPercent = (v) => {
  const n = Number(v);
  if (v === undefined || v === null || v === '' || !Number.isFinite(n)) return NOT_SET;
  if (n <= 0) return PCT_BANDS[0];
  if (n < 40) return PCT_BANDS[1];
  if (n < 75) return PCT_BANDS[2];
  return PCT_BANDS[3];
};

const yearOf = (v) => {
  const m = String(v === undefined || v === null ? '' : v).match(/(\d{4})/);
  return m ? m[1] : NOT_SET;
};

// ---- dimension catalogue ---------------------------------------------------
// Reasons a schema field cannot be a heat-map dimension. Shown in the menu; the
// option is disabled, never hidden.
const UNCHARTABLE = {
  [FIELD_TYPES.TEXTAREA]: 'long-form text — not a bounded set',
  [FIELD_TYPES.NUMBER]: 'continuous number — not a bounded set',
  [FIELD_TYPES.CURRENCY]: 'currency amount — not a bounded set',
  [FIELD_TYPES.LIFECYCLE]: 'timeline field — group by Lifecycle State instead',
  [FIELD_TYPES.RELATION]: 'relationship, not a value',
  [FIELD_TYPES.TAGS]: 'use the "Tag: …" dimensions',
};

const BOUNDED_TYPES = [
  FIELD_TYPES.SELECT, FIELD_TYPES.MULTISELECT, FIELD_TYPES.BOOLEAN,
  FIELD_TYPES.ARROWS, FIELD_TYPES.RATING,
];

function buildDimensions(typeKey, tagGroups) {
  const out = [];

  // Synthetic dimensions that every fact sheet carries.
  out.push({
    key: '__type',
    label: 'Fact Sheet Type',
    section: 'Core',
    kind: 'bounded',
    order: FACT_SHEET_TYPES.map((t) => t.label),
    get: (fs) => (fs.type ? typeMeta(fs.type).label : NOT_SET),
  });
  out.push({
    key: '__drTier',
    label: 'DR Tier (tag + field reconciled)',
    section: 'Core',
    kind: 'bounded',
    ordinal: true,
    order: PICKLISTS.tier,
    get: (fs) => resolveDrTier(fs) || NOT_SET,
  });
  out.push({
    key: '__completion',
    label: 'Data Completion',
    section: 'Core',
    kind: 'bounded',
    ordinal: true,
    order: PCT_BANDS,
    get: (fs) => bandPercent(fs.completion),
  });

  // Tag groups. "DR Tier" is deliberately absent — reading the tag alone drops
  // every record that carries the value in fields.drTier, so the reconciled
  // __drTier dimension above is the only DR Tier on offer.
  tagGroups.filter((g) => g !== 'DR Tier').forEach((g) => {
    out.push({
      key: `__tag:${g}`,
      label: `Tag: ${g}`,
      section: 'Tags',
      kind: 'bounded',
      get: (fs) => tagText(fs, g),
    });
  });

  // Schema fields for the selected type (or the union across all types).
  const typeKeys = typeKey === ALL_TYPES ? FACT_SHEET_TYPES.map((t) => t.key) : [typeKey];
  const seen = { name: true, drTier: true }; // name = identity; drTier = folded above
  typeKeys.forEach((tk) => {
    allFieldsFor(tk).forEach((f) => {
      if (seen[f.key]) return;
      seen[f.key] = true;
      const base = { key: f.key, label: f.label, section: f.section || 'Fields' };
      const picklist = f.picklist ? resolvePicklist(f.picklist) : null;

      if (BOUNDED_TYPES.indexOf(f.type) !== -1) {
        out.push({
          ...base,
          kind: 'bounded',
          ordinal: f.type === FIELD_TYPES.RATING || f.type === FIELD_TYPES.ARROWS
            || ORDINAL_KEYS.indexOf(f.key) !== -1,
          boolean: f.type === FIELD_TYPES.BOOLEAN,
          order: f.type === FIELD_TYPES.BOOLEAN ? ['Yes', 'No'] : (picklist || null),
          get: (fs) => fieldText(fs, f.key),
        });
        return;
      }
      if (f.type === FIELD_TYPES.PERCENT) {
        out.push({
          ...base, kind: 'bounded', ordinal: true, order: PCT_BANDS,
          get: (fs) => bandPercent(fs.fields ? fs.fields[f.key] : undefined),
        });
        return;
      }
      if (f.type === FIELD_TYPES.DATE) {
        out.push({
          ...base, label: `${f.label} (year)`, kind: 'bounded',
          get: (fs) => yearOf(fs.fields ? fs.fields[f.key] : undefined),
        });
        return;
      }
      if (f.type === FIELD_TYPES.TEXT || f.type === FIELD_TYPES.URL) {
        // Legitimate dimensions (Business Owner, Vendor, Business Unit…) but
        // only when the tenant's data keeps them to a chartable number of
        // distinct values. Decided against the live data, not guessed here.
        out.push({ ...base, kind: 'text', get: (fs) => fieldText(fs, f.key) });
        return;
      }
      out.push({
        ...base,
        kind: 'unavailable',
        reason: UNCHARTABLE[f.type] || `${f.type} field — not a bounded set`,
        get: () => NOT_SET,
      });
    });
  });

  return out;
}

// ---- colouring -------------------------------------------------------------
function buildColorMap(dim, values) {
  const map = {};
  const order = dim && dim.order && dim.order.length ? dim.order : null;
  const semantic = dim ? SEMANTIC_COLORS[dim.key] : null;
  let next = 0;
  values.forEach((v) => {
    if (v === NOT_SET) { map[v] = NOT_SET_COLOR; return; }
    if (semantic && semantic[v]) { map[v] = semantic[v]; return; }
    if (dim.ordinal && order) {
      const i = order.indexOf(v);
      if (i >= 0) { map[v] = scaleColor(i, order.length); return; }
      map[v] = UNKNOWN_COLOR; // a value the picklist does not know about
      return;
    }
    if (dim.boolean) { map[v] = v === 'Yes' ? BLUE : HEADER; return; }
    map[v] = PALETTE[next % PALETTE.length];
    next += 1;
  });
  return map;
}

// Sort values by picklist order when we have one, otherwise by frequency.
// "Not set" always leads: it is the most actionable bucket in discovery and
// pinning it first also guarantees the group cap can never hide it.
function sortByOrder(entries, order) {
  const idx = (v) => {
    if (v === NOT_SET) return -1;
    const i = order ? order.indexOf(v) : -1;
    return i >= 0 ? i : 5000;
  };
  return [...entries].sort((a, b) => {
    const d = idx(a.value) - idx(b.value);
    if (d !== 0) return d;
    return (b.count - a.count) || a.value.localeCompare(b.value);
  });
}

const pct = (n, total) => (total ? Math.round((n / total) * 1000) / 10 : 0);
const num = (n) => Number(n || 0).toLocaleString();

// Inline styles (not sx) for anything colour-bearing inside the print region:
// printElementToPdf() copies innerHTML into a bare window, where emotion's
// class names no longer resolve. An exported heat map with no colour is not a
// deliverable.
const swatchStyle = (bg, value) => ({
  backgroundColor: bg,
  color: value === NOT_SET ? '#1F2A36' : '#fff',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
});

// ---------------------------------------------------------------------------
export default function LandscapeReport({ tenantId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeKey, setTypeKey] = useState('Application');
  const [groupBy, setGroupBy] = useState('applicationType');
  const [colorBy, setColorBy] = useState('timeClassification');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    const filter = typeKey === ALL_TYPES ? {} : { type: typeKey };
    store.listFactSheets(tenantId, filter)
      .then((list) => { if (alive) { setRows(list || []); setLoading(false); } })
      .catch((e) => { if (alive) { setError(String((e && e.message) || e)); setRows([]); setLoading(false); } });
    return () => { alive = false; };
  }, [tenantId, typeKey]);

  // Tag groups actually present in the tenant, plus the governed list.
  const tagGroups = useMemo(() => {
    const found = {};
    TAG_GROUPS.forEach((g) => { found[g] = true; });
    rows.forEach((fs) => (fs.tags || []).forEach((t) => { if (t && t.group) found[t.group] = true; }));
    return Object.keys(found);
  }, [rows]);

  const dimensions = useMemo(() => buildDimensions(typeKey, tagGroups), [typeKey, tagGroups]);

  // ---- distinct-value analysis --------------------------------------------
  // ONE pass over the row set (7,305 fact sheets in the largest tenant) with
  // every candidate dimension on the inner loop — never one scan per dimension,
  // and never in render. The counts decide three things at once: which
  // free-text fields are chartable, which dimensions this tenant has simply not
  // populated, and which will overflow the group cap.
  const options = useMemo(() => {
    const measured = dimensions.filter((d) => d.kind !== 'unavailable');
    const sets = measured.map(() => new Set());
    for (let i = 0; i < rows.length; i += 1) {
      for (let j = 0; j < measured.length; j += 1) {
        const v = measured[j].get(rows[i]);
        if (v !== NOT_SET) sets[j].add(v);
      }
    }
    const distinct = {};
    measured.forEach((d, j) => { distinct[d.key] = sets[j].size; });

    return dimensions.map((d) => {
      if (d.kind === 'unavailable') return { ...d, disabled: true, note: d.reason, distinct: 0 };
      const n = distinct[d.key] || 0;
      const base = { ...d, distinct: n };
      if (n === 0) {
        return { ...base, disabled: true, note: 'no values captured in this workspace yet' };
      }
      if (d.kind === 'text' && n > TEXT_DISTINCT_LIMIT) {
        return { ...base, disabled: true, note: `${num(n)} distinct values — too many to chart` };
      }
      if (n > MAX_GROUPS) {
        return { ...base, disabled: false, note: `${num(n)} distinct values — top ${MAX_GROUPS} charted` };
      }
      return { ...base, disabled: false, note: `${n} distinct values` };
    });
  }, [dimensions, rows]);

  const enabled = useMemo(() => options.filter((o) => !o.disabled), [options]);
  const byKey = useMemo(() => {
    const m = {};
    options.forEach((o) => { m[o.key] = o; });
    return m;
  }, [options]);

  // A dimension can become unavailable when the type filter or the data
  // changes. Fall back visibly rather than rendering a blank chart.
  const groupDim = (byKey[groupBy] && !byKey[groupBy].disabled) ? byKey[groupBy] : enabled[0];
  const colorDim = (byKey[colorBy] && !byKey[colorBy].disabled) ? byKey[colorBy] : enabled[0];
  const groupFellBack = !!(groupDim && groupDim.key !== groupBy);
  const colorFellBack = !!(colorDim && colorDim.key !== colorBy);

  // ---- grouping ------------------------------------------------------------
  const model = useMemo(() => {
    const empty = {
      groups: [], hidden: [], hiddenRecords: 0, charted: 0, total: rows.length,
      legend: [], colorMap: {}, colorTotals: [],
    };
    if (!groupDim || !colorDim) return empty;

    const buckets = new Map();
    const colorCounts = new Map();
    for (let i = 0; i < rows.length; i += 1) {
      const fs = rows[i];
      const g = groupDim.get(fs);
      let b = buckets.get(g);
      if (!b) { b = { value: g, items: [], colors: new Map() }; buckets.set(g, b); }
      b.items.push(fs);
      const c = colorDim.get(fs);
      b.colors.set(c, (b.colors.get(c) || 0) + 1);
      colorCounts.set(c, (colorCounts.get(c) || 0) + 1);
    }

    const all = sortByOrder(
      [...buckets.values()].map((b) => ({ ...b, count: b.items.length })),
      groupDim.order,
    );
    const groups = all.slice(0, MAX_GROUPS);
    const hidden = all.slice(MAX_GROUPS);
    const charted = groups.reduce((s, g) => s + g.count, 0);
    const hiddenRecords = hidden.reduce((s, g) => s + g.count, 0);

    const colorTotals = sortByOrder(
      [...colorCounts.entries()].map(([value, count]) => ({ value, count })),
      colorDim.order,
    );
    const colorMap = buildColorMap(colorDim, colorTotals.map((c) => c.value));

    return {
      groups, hidden, hiddenRecords, charted, total: rows.length,
      colorTotals, colorMap, legend: colorTotals.slice(0, MAX_LEGEND),
    };
  }, [rows, groupDim, colorDim]);

  const typeLabel = typeKey === ALL_TYPES ? 'all fact sheet types' : `${typeMeta(typeKey).label} fact sheets`;
  const basis = `Group by ${groupDim ? groupDim.label : '—'} · colour by ${colorDim ? colorDim.label : '—'}`;
  const chartedLine = `${num(model.charted)} of ${num(model.total)} ${typeLabel} charted`;

  // ---- exports -------------------------------------------------------------
  // Both artifacts carry the basis (dimension, measure, counts) — a heat map
  // exported without its legend is not a client deliverable.
  const csvColumns = [
    { label: 'Group-by dimension', value: 'dim' },
    { label: 'Group', value: 'group' },
    { label: 'Records in group', value: 'records' },
    { label: '% of records in scope', value: 'share' },
    { label: 'Colour measure', value: 'measure' },
    { label: 'Colour value', value: 'colorValue' },
    { label: 'Records with colour value', value: 'colorRecords' },
  ];

  const downloadCsv = () => {
    if (!groupDim || !colorDim) return;
    const dim = groupDim.label;
    const measure = colorDim.label;
    const out = [{
      dim,
      group: '(basis)',
      records: model.total,
      share: '100%',
      measure,
      colorValue: `${chartedLine}; ${model.groups.length} groups charted`
        + `${model.hidden.length ? `, ${model.hidden.length} more groups in this file only` : ''}`,
      colorRecords: new Date().toISOString(),
    }];
    // Every group goes to CSV, including any beyond the on-screen cap.
    [...model.groups, ...model.hidden].forEach((g) => {
      sortByOrder(
        [...g.colors.entries()].map(([value, count]) => ({ value, count })),
        colorDim.order,
      ).forEach((c) => {
        out.push({
          dim,
          group: g.value,
          records: g.count,
          share: `${pct(g.count, model.total)}%`,
          measure,
          colorValue: c.value,
          colorRecords: c.count,
        });
      });
    });
    out.push({
      dim, group: 'TOTAL', records: model.total, share: '100%',
      measure, colorValue: '(all values)', colorRecords: model.total,
    });
    exportCsv(`landscape-${groupDim.key}-by-${colorDim.key}.csv`.replace(/[^a-zA-Z0-9.\-_]/g, '_'), out, csvColumns);
  };

  const downloadPdf = () => printElementToPdf('landscape-print', `Application Landscape — ${basis}`);

  // ---- menu ----------------------------------------------------------------
  // Flat MenuItems only: this MUI version clones every Select child with
  // role="option" and a click handler, so a ListSubheader would render as a
  // selectable option carrying an undefined value. Section shown inline
  // instead, the same way SavedViews renders its field catalogue.
  const menuItems = (dims) => dims.map((d) => (
    <MenuItem key={d.key} value={d.key} disabled={d.disabled} sx={{ fontSize: 13 }}>
      {d.label}
      <span style={{ color: '#9AA6B2', fontSize: 11, marginLeft: 8 }}>
        {d.section}
        {d.note ? ` · ${d.note}` : ''}
      </span>
    </MenuItem>
  ));

  const legendRow = (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {model.legend.map((c) => (
        <Box
          key={c.value}
          style={swatchStyle(model.colorMap[c.value], c.value)}
          sx={{
            px: 0.75, py: 0.25, borderRadius: 3, fontSize: 11, fontWeight: 600,
            border: c.value === NOT_SET ? '1px dashed #5C6B7A' : '1px solid transparent',
          }}
        >
          {c.value} ({num(c.count)})
        </Box>
      ))}
      {model.colorTotals.length > MAX_LEGEND && (
        <Typography variant="caption" sx={{ color: GREY }}>
          +{model.colorTotals.length - MAX_LEGEND} more {colorDim ? colorDim.label : ''} values
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEADER }}>Application Landscape</Typography>
        <HelpBubble topic="landscapeReport" />
        <TextField
          select size="small" label="Fact sheet type" value={typeKey}
          onChange={(e) => setTypeKey(e.target.value)} sx={{ minWidth: 170 }}
        >
          <MenuItem value={ALL_TYPES}>All types</MenuItem>
          {FACT_SHEET_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Group by" value={groupDim ? groupDim.key : ''}
          onChange={(e) => setGroupBy(e.target.value)}
          sx={{ minWidth: 260 }}
          SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
        >
          {menuItems(options)}
        </TextField>
        <TextField
          select size="small" label="Colour by" value={colorDim ? colorDim.key : ''}
          onChange={(e) => setColorBy(e.target.value)}
          sx={{ minWidth: 260 }}
          SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
        >
          {menuItems(options)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadCsv} disabled={!model.total}>CSV</Button>
        <Button startIcon={<PictureAsPdfIcon />} variant="outlined" onClick={downloadPdf} disabled={!model.total}>PDF</Button>
      </Stack>

      <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1.5 }}>
        {enabled.length} of {options.length} dimensions are chartable in this workspace; the rest stay
        listed with the reason they are unavailable. Free-text fields are offered up to&nbsp;
        {TEXT_DISTINCT_LIMIT} distinct values.
        {groupFellBack && ` Requested group-by dimension is unavailable here — showing ${groupDim.label}.`}
        {colorFellBack && ` Requested colour measure is unavailable here — showing ${colorDim.label}.`}
      </Typography>

      {loading && <Paper sx={{ p: 4, textAlign: 'center', color: GREY }}>Loading fact sheets…</Paper>}

      {!loading && !!error && (
        <Paper sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, color: '#8a2118', mb: 0.5 }}>Could not load fact sheets</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>{error} — nothing is charted, so this screen is showing no estate at all rather than a partial one.</Typography>
        </Paper>
      )}

      {!loading && !error && !model.total && (
        <Paper sx={{ p: 6, textAlign: 'center', color: GREY }}>
          <Typography sx={{ fontWeight: 700, color: HEADER, mb: 0.5 }}>Nothing to plot yet</Typography>
          <Typography variant="body2">No {typeLabel} in this workspace. Import or create some to build the landscape.</Typography>
        </Paper>
      )}

      {!loading && !error && !!model.total && groupDim && colorDim && (
        <div id="landscape-print">
          <Paper sx={{ p: 1.5, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
              Application Landscape — {basis}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip size="small" label={chartedLine} sx={{ height: 22, bgcolor: '#e8f0fe', fontWeight: 700 }} />
              <Chip size="small" label={`${model.groups.length} group${model.groups.length === 1 ? '' : 's'} shown`} sx={{ height: 22 }} />
              {!!model.hidden.length && (
                <Chip size="small" label={`+${model.hidden.length} more groups (${num(model.hiddenRecords)} records) not charted`} sx={{ height: 22, bgcolor: '#fdecea', color: '#8a2118' }} />
              )}
            </Stack>
            {legendRow}
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.75 }}>
              &ldquo;{NOT_SET}&rdquo; counts records with no value for this dimension — they are charted,
              never dropped. Group sizes are exact; tiles are capped at {MAX_TILES_PER_GROUP} per group.
            </Typography>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
            {model.groups.map((g) => {
              const shown = g.items.slice(0, MAX_TILES_PER_GROUP);
              const breakdown = sortByOrder(
                [...g.colors.entries()].map(([value, count]) => ({ value, count })),
                colorDim.order,
              );
              return (
                <Paper
                  key={g.value}
                  sx={{
                    p: 1.5,
                    border: g.value === NOT_SET ? '1px dashed #9AA6B2' : '1px solid #eef2f6',
                    bgcolor: g.value === NOT_SET ? '#fafbfc' : '#fff',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>{g.value}</Typography>
                    <Chip size="small" label={`${num(g.count)} · ${pct(g.count, model.total)}%`} sx={{ height: 18, fontSize: 11 }} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 0.75 }}>
                    {breakdown.slice(0, 4).map((c) => `${c.value}: ${num(c.count)}`).join(' · ')}
                    {breakdown.length > 4 ? ` · +${breakdown.length - 4} more` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {/* Native title rather than MUI Tooltip: a 7,305-app tenant
                        would otherwise mount thousands of popper instances. */}
                    {shown.map((a) => {
                      const cv = colorDim.get(a);
                      return (
                        <Box
                          key={a.id}
                          onClick={() => onOpen && onOpen(a.id)}
                          title={`${a.name} — ${groupDim.label}: ${g.value} · ${colorDim.label}: ${cv} · ${a.completion || 0}% complete`}
                          style={swatchStyle(model.colorMap[cv] || UNKNOWN_COLOR, cv)}
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            borderRadius: 0.5,
                            fontSize: 10,
                            fontWeight: 600,
                            cursor: 'pointer',
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            border: cv === NOT_SET ? '1px dashed #5C6B7A' : '1px solid transparent',
                            '&:hover': { opacity: 0.85 },
                          }}
                        >
                          {a.name}
                        </Box>
                      );
                    })}
                  </Box>
                  {g.count > shown.length && (
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.75, fontStyle: 'italic' }}>
                      +{num(g.count - shown.length)} more records in this group (not drawn as tiles; counted above and in the CSV)
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Box>

          {!!model.hidden.length && (
            <Paper sx={{ p: 1.5, mt: 2, bgcolor: '#fafbfc' }}>
              <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13, mb: 0.5 }}>
                +{model.hidden.length} more {groupDim.label} groups ({num(model.hiddenRecords)} records) beyond the {MAX_GROUPS}-group limit
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {model.hidden.slice(0, 30).map((g) => (
                  <Tooltip key={g.value} title={`${num(g.count)} records`}>
                    <Chip size="small" label={`${g.value} (${num(g.count)})`} sx={{ height: 20, fontSize: 11 }} />
                  </Tooltip>
                ))}
                {model.hidden.length > 30 && (
                  <Typography variant="caption" sx={{ color: GREY }}>+{model.hidden.length - 30} more</Typography>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                Every group is included in the CSV export. Pick a coarser dimension to chart them all.
              </Typography>
            </Paper>
          )}
        </div>
      )}
    </Box>
  );
}
